/**
 * ReplyTemplatesSection (TMPL-REPLY) — Settings → Templates → "Reply templates".
 * The canned texts/emails the Inbox composer inserts (Payment, AC Plus Plan
 * Scheduling, …). Imported once from Podium; Pegasus now OWNS them — add / edit /
 * archive here, no re-import. `:contact_first_name:` is swapped for the
 * customer's name when the dispatcher inserts one.
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Pencil, Plus, MessageSquare, Mail } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { MergeFieldMenu } from "@/lib/mergeFields";

interface ReplyTemplate {
  id: string;
  title: string;
  body: string;
  subject: string | null;
  channel: string;
  access_level: string | null;
  sort: number | null;
  archived: boolean;
}

function useReplyTemplates() {
  return useQuery({
    queryKey: ["reply_templates_admin"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ReplyTemplate[]> => {
      const { data, error } = await supabase
        .from("reply_template")
        .select("id, title, body, subject, channel, access_level, sort, archived")
        .order("sort", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ReplyTemplate[];
    },
  });
}

export function ReplyTemplatesSection() {
  const canManage = useCan("settings.manage");
  const { data, isLoading } = useReplyTemplates();
  const qc = useQueryClient();
  const all = useMemo(() => data ?? [], [data]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<ReplyTemplate | "new" | null>(null);

  const visible = all.filter((t) => showArchived || !t.archived);

  const setArchived = useMutation({
    mutationFn: async (v: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("reply_template")
        .update({ archived: v.archived, updated_at: new Date().toISOString() })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reply_templates_admin"] }),
  });

  if (isLoading) return <div className="h-64 animate-pulse rounded-xl bg-slate-100" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-500">
          {visible.length} template{visible.length === 1 ? "" : "s"}
          <InfoTip label="About Inbox templates">
            The quick replies your team inserts in the Inbox when texting or emailing a customer.{" "}
            <code>:contact_first_name:</code> becomes the customer's first name on insert.
          </InfoTip>
        </span>
        {canManage && (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
          >
            <Plus className="h-3.5 w-3.5" /> New template
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-hairline bg-white shadow-e1">
        <ul className="divide-y divide-slate-100">
          {visible.map((t) => (
            <li key={t.id} className={cn("group flex items-start gap-3 px-4 py-3", t.archived && "opacity-50")}>
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                {t.channel === "email" ? <Mail className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900">{t.title}</span>
                  {t.archived && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-400">archived</span>
                  )}
                </div>
                {t.subject && <div className="truncate text-[11px] text-slate-500">Subject: {t.subject}</div>}
                <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-500">{t.body}</div>
              </div>
              {canManage && (
                <div className="flex shrink-0 items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditing(t)}
                    title="Edit"
                    className="rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    disabled={setArchived.isPending}
                    onClick={() => setArchived.mutate({ id: t.id, archived: !t.archived })}
                    title={t.archived ? "Restore — usable in the composer again" : "Archive — hidden from the composer (never deleted)"}
                    className="rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    {t.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                  </button>
                </div>
              )}
            </li>
          ))}
          {visible.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-slate-400">No reply templates yet.</li>
          )}
        </ul>
      </div>

      <label className="flex cursor-pointer items-center gap-1.5 px-1 text-[11px] text-slate-400">
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3 w-3 accent-brand-700" />
        Show archived
      </label>

      {editing && (
        <ReplyTemplateEditor
          template={editing === "new" ? null : editing}
          nextSort={(Math.max(0, ...all.map((t) => t.sort ?? 0)) + 10)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ReplyTemplateEditor({
  template,
  nextSort,
  onClose,
}: {
  template: ReplyTemplate | null;
  nextSort: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(template?.title ?? "");
  const [channel, setChannel] = useState(template?.channel ?? "both");
  const [subject, setSubject] = useState(template?.subject ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [err, setErr] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const insertToken = (token: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const row = {
        title: title.trim(),
        channel,
        subject: subject.trim() || null,
        body: body.trim(),
        updated_by: u.user?.email ?? null,
        updated_at: new Date().toISOString(),
      };
      if (template) {
        const { error } = await supabase.from("reply_template").update(row).eq("id", template.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("reply_template").insert({ ...row, source: "pegasus", sort: nextSort, archived: false });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reply_templates_admin"] });
      qc.invalidateQueries({ queryKey: ["reply_templates"] }); // the composer picker
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={template ? `Edit “${template.title}”` : "New reply template"}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { setErr(null); save.mutate(); }}
            disabled={!title.trim() || !body.trim() || save.isPending}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {save.isPending ? "Saving…" : template ? "Save changes" : "Add template"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-2 block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Title</span>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="Furnace Tune Up Pricing" className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm" />
          </label>
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Use for</span>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm">
              <option value="both">Text & email</option>
              <option value="sms">Text only</option>
              <option value="email">Email only</option>
            </select>
          </label>
        </div>
        {channel !== "sms" && (
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Subject (email)</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="optional" className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm" />
          </label>
        )}
        {/* A <div>, not a <label>: a label would hand its clicks to the Insert Field button inside it. */}
        <div className="block text-xs text-slate-600">
          <span className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <label htmlFor="reply-template-body">Message</label>
            <span className="normal-case"><MergeFieldMenu onInsert={insertToken} label="Insert field" /></span>
          </span>
          <textarea id="reply-template-body" ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={7} placeholder="Hi :contact_first_name:, this is Sedgwick Heating…" className="w-full resize-y rounded-md border border-hairline px-2 py-1.5 text-sm leading-snug" />
          <span className="mt-1 block text-[11px] text-slate-400">
            <code className="rounded bg-slate-100 px-1">:contact_first_name:</code> becomes the customer's first name when inserted.
          </span>
        </div>
        {err && <p className="rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">{err}</p>}
      </div>
    </Modal>
  );
}
